/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    getFriendlyName,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    IRequestContext
} from '@msdyn365-commerce/core';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    buildCacheKeyWithUrlTokens,
    CurrentCategoryInput,
    getCategoryUrl,
    getCurrentCategory,
    removeDomainQspFromUrl
} from '@msdyn365-commerce-modules/retail-actions';
import { IDefaultPageSummaryConfig } from '@msdyn365-commerce-modules/page-summary';
import { IPageSummaryData } from '@msdyn365-commerce-modules/page-summary';
/**
 * Category Page Summary Input
 */
export class CategoryPageSummaryInput implements IActionInput {
    public requestContext: IRequestContext;
    public config: IDefaultPageSummaryConfig;
    constructor(config: IDefaultPageSummaryConfig, requestContext: IRequestContext) {
        this.config = config || {};
        this.requestContext = requestContext;
    }
    public getCacheObjectType = (): string => 'CategoryPageSummary';
    public getCacheKey = (): string => buildCacheKeyWithUrlTokens('CategoryPageSummary', this.requestContext);
    public dataCacheType = (): CacheType => 'request';
}
const createGetCurrentCategoryInput = (inputData: IActionContext): CurrentCategoryInput => {
    if (inputData && inputData.requestContext) {
        return new CurrentCategoryInput(inputData.requestContext);
    }
    throw new Error('Please specify categoryId query string in request.');
};
/**
 * Get skip count.
 * @param inputData
 */
function getSkipCount(inputData: ICreateActionContext<IGeneric<IAny>, IGeneric<IAny>>): string | undefined {
    return inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.skip;
}
const action = async (input: CategoryPageSummaryInput, context: IActionContext): Promise<IPageSummaryData> => {
    const { config } = input;
    // const jsonUrl =
    //     context.requestContext.app.config.plpSeoJsonUrl ||
    //     'https://files-eu-prod.cms.commerce.dynamics.com/cms/api/ctdwmwnctb/binary/MD4Pav';
    const excelUrl =
        context.requestContext.app.config.plpSeoTSVUrl ||
        'https://files-eu-prod.cms.commerce.dynamics.com/cms/api/ctdwmwnctb/binary/MD8w5X';
    // const res = await Msdyn365.sendRequest<any>(jsonUrl, 'get');
    const excelRes = await Msdyn365.sendRequest<any>(excelUrl, 'get');
    let currentCategoryResult;
    try {
        currentCategoryResult = await getCurrentCategory(createGetCurrentCategoryInput(context), context);
    } catch {
        // Do nothing, if the action fails fallback to values defined in data
    }
    const currCatUrl = context.requestContext.url.requestUrl.href;
    let configCategoryUrl = '';
    let configCategoryName = '';
    let configCategoryDesc = '';
    if (
        context.requestContext.app.config.canonicalUrlForCategories &&
        Array.isArray(context.requestContext.app.config.canonicalUrlForCategories)
    ) {
        for (const canonicalUrlCatConfig of context.requestContext.app.config.canonicalUrlForCategories) {
            if (currCatUrl.includes(canonicalUrlCatConfig.categoryIDOrUrl) || currCatUrl === canonicalUrlCatConfig.categoryIDOrUrl) {
                configCategoryUrl = canonicalUrlCatConfig.canonicalUrl;
                configCategoryName = canonicalUrlCatConfig.canonicalName;
                configCategoryDesc = canonicalUrlCatConfig.canonicalDescription;
            }
        }
    }
    if (currentCategoryResult) {
        const currentCategory = { ...currentCategoryResult };
        let categoryUrl: string | undefined;
        try {
            // Reset category's name neutralizedName
            const localName = getFriendlyName(context.requestContext.locale, currentCategory.NameTranslations);
            const neutralizedName = currentCategory.NeutralizedName || currentCategory.Name;
            currentCategory.Name = localName;
            currentCategory.NeutralizedName = neutralizedName;
            categoryUrl = getCategoryUrl(currentCategory);
            const canonicalDomain = context.requestContext.canonicalDomain;
            if (categoryUrl && canonicalDomain) {
                categoryUrl = `https://${canonicalDomain}${categoryUrl}`.toLocaleLowerCase();
                const skipCount = getSkipCount(context);
                const Url = new URL(categoryUrl);
                const urlParams: URLSearchParams = new URLSearchParams(Url.search);
                if (skipCount) {
                    urlParams.set('skip', skipCount);
                }
                Url.search = urlParams.toString();
                categoryUrl = Url.toString();
                categoryUrl = removeDomainQspFromUrl(categoryUrl, context.requestContext);
            } else {
                categoryUrl = undefined;
            }
        } catch {
            categoryUrl = undefined;
        }
        const catUrl = getCategoryUrl(currentCategory);
        const excelDataStr = excelRes.data as string;
        const splittedExcelData = excelDataStr.split('\n');
        let title = currentCategory.Name;
        let desc;
        let canonical = categoryUrl;
        for (const dataRow of splittedExcelData) {
            if (!dataRow || !dataRow.trim()) {
                continue;
            }
            const rowSplitted = dataRow.trim().split('\t');
            const curl = rowSplitted[0];
            if (catUrl && (catUrl === curl || catUrl?.includes(curl) || curl.includes(catUrl))) {
                title = rowSplitted.length > 1 ? rowSplitted[1] : currentCategory.Name;
                desc = rowSplitted.length > 2 ? rowSplitted[2] : undefined;
                canonical = rowSplitted.length > 3 ? rowSplitted[3] : categoryUrl;
                return {
                    title: title,
                    description: desc,
                    sharingImageUrl: currentCategory.OfflineImage,
                    canonicalUrl: canonical,
                    faviconUrl: config && config.faviconUrl
                };
            }
        }
        return {
            title: title,
            description: desc,
            sharingImageUrl: currentCategory.OfflineImage,
            canonicalUrl: canonical,
            faviconUrl: config && config.faviconUrl
        };
        // const plpSeoData = res.data && catUrl && res.data[catUrl];
        // return {
        //     title: plpSeoData ? plpSeoData.title : currentCategory.Name,
        //     description: plpSeoData && plpSeoData.description,
        //     sharingImageUrl: currentCategory.OfflineImage,
        //     canonicalUrl: plpSeoData ? plpSeoData.canonicalUrl : categoryUrl,
        //     faviconUrl: config && config.faviconUrl
        // };
        // If the action fails fallback to values defined from data
    } else if (configCategoryUrl && configCategoryName) {
        return {
            title: configCategoryName,
            description: configCategoryDesc,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            canonicalUrl: configCategoryUrl,
            faviconUrl: config && config.faviconUrl
        };
    } else if (config) {
        return {
            title: config.title,
            sharingImageUrl: config.sharingImage && config.sharingImage.src,
            faviconUrl: config && config.faviconUrl
        };
    }
    return {};
};
export default createObservableDataAction({
    id: '@msdyn365-commerce-modules/page-summary/category-page-summary',
    action: <IAction<IPageSummaryData>>action,
    input: (args: ICreateActionContext) => {
        return new CategoryPageSummaryInput(<IDefaultPageSummaryConfig>args.config, args.requestContext);
    }
});
